const DEFAULT_CONTRACT = Object.freeze({
  status: Object.freeze(["Source Status", "source_status", "Event Status", "event_status", "Status", "status"]),
  start: Object.freeze(["Game Start Timestamp", "gameStartTimestamp", "game_start_timestamp", "Event Start Timestamp", "event_start_timestamp"]),
  replacementStart: Object.freeze(["Replacement Game Start Timestamp", "replacement_game_start_timestamp"]),
});

const BLOCKED_STATUSES = new Set([
  "IN_GAME", "LIVE", "STARTED", "FINAL", "COMPLETED", "CANCELED", "CANCELLED",
  "ABANDONED", "SUSPENDED",
]);

const nonempty = (value) => value !== null && value !== undefined && String(value).trim() !== "";
const sportOf = (row, fallbackSport) => String(row?.Sport || fallbackSport || "").trim().toUpperCase();

export const lineupContractFor = (sport) => String(sport || "").trim() ? DEFAULT_CONTRACT : null;

const firstPresent = (row, fields) => fields.find((field) => nonempty(row?.[field]));

const authoritativeStart = (row, fields) => {
  const values = fields.filter((field) => nonempty(row?.[field])).map((field) => String(row[field]).trim());
  if (!values.length || values.some((value) => !/[zZ]|[+-]\d\d:\d\d$/.test(value))) {
    return { valid: false, reason: "AUTHORITATIVE_START_MISSING_OR_INVALID" };
  }
  const instants = values.map((value) => Date.parse(value));
  if (instants.some((value) => !Number.isFinite(value))) {
    return { valid: false, reason: "AUTHORITATIVE_START_MISSING_OR_INVALID" };
  }
  if (new Set(instants).size !== 1) return { valid: false, reason: "AUTHORITATIVE_START_CONTRADICTORY" };
  return { valid: true, startMs: instants[0] };
};

export const lineupPlayerKey = (row, fallbackSport) => {
  const sport = sportOf(row, fallbackSport);
  const playerId = row?.["Player ID"] ?? row?.PlayerID ?? row?.player_id;
  return sport && nonempty(playerId) ? `${sport}:${String(playerId).trim()}` : "";
};

export const lineupProjectionKey = (row) => {
  const projectionId = row?.["Projection ID"] ?? row?.ProjectionID ?? row?.projection_id;
  return nonempty(projectionId) ? String(projectionId).trim() : "";
};

export function evaluateLineupProjection(row, { sport, now = new Date() } = {}) {
  const normalizedSport = sportOf(row, sport);
  const contract = lineupContractFor(normalizedSport);
  if (!contract) return { eligible: false, reason: "LINEUP_CONTRACT_UNSUPPORTED" };
  if (!lineupProjectionKey(row)) return { eligible: false, reason: "PROJECTION_ID_MISSING" };
  if (!lineupPlayerKey(row, normalizedSport)) return { eligible: false, reason: "PLAYER_ID_MISSING" };
  if (!nonempty(row?.Player) || !nonempty(row?.Team) || !nonempty(row?.Opponent)) {
    return { eligible: false, reason: "PLAYER_EVENT_IDENTITY_INCOMPLETE" };
  }
  const eventId = row?.["Game ID"] ?? row?.GameID ?? row?.game_id;
  if (!nonempty(eventId)) return { eligible: false, reason: "GAME_ID_MISSING" };

  const statusField = firstPresent(row, contract.status);
  const status = String(statusField ? row?.[statusField] : "").trim().toUpperCase().replace(/[ -]+/g, "_");
  if (BLOCKED_STATUSES.has(status)) return { eligible: false, reason: "EVENT_STATUS_BLOCKED" };

  const startFields = status === "POSTPONED" ? contract.replacementStart : contract.start;
  const start = authoritativeStart(row, startFields);
  if (!start.valid) {
    return { eligible: false, reason: status === "POSTPONED" ? "POSTPONED_WITHOUT_CONFIRMED_REPLACEMENT" : start.reason };
  }
  const startMs = start.startMs;
  const nowMs = now instanceof Date ? now.getTime() : new Date(now).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(nowMs)) return { eligible: false, reason: "AUTHORITATIVE_START_MISSING_OR_INVALID" };
  if (startMs <= nowMs) return { eligible: false, reason: "EVENT_ALREADY_STARTED" };

  return { eligible: true, reason: "UPCOMING_AUTHORITATIVE_START", sport: normalizedSport, startMs };
}

export function eligibleLineupPool(rows, options = {}) {
  const eligible = [];
  const rejected = [];
  for (const row of Array.isArray(rows) ? rows : []) {
    const decision = evaluateLineupProjection(row, options);
    (decision.eligible ? eligible : rejected).push({ row, decision });
  }
  return { eligible: eligible.map(({ row }) => row), rejected };
}

export function lineupStillEligible(lineup, options = {}) {
  const playerKeys = new Set();
  for (const row of Array.isArray(lineup) ? lineup : []) {
    if (!evaluateLineupProjection(row, options).eligible) return false;
    const key = lineupPlayerKey(row, options.sport);
    if (!key || playerKeys.has(key)) return false;
    playerKeys.add(key);
  }
  return playerKeys.size > 0;
}
